<template>
  <div class="flex min-h-72">
    <BaseCard class="flex flex-col flex-1">
      <RouterLink :to="to" class="flex flex-col flex-1">
        <BaseCardBody>
          <h3 class="text-sm">{{ project.name }}</h3>
        </BaseCardBody>
        <div class="mt-auto">
          <template v-if="project.cover">
            <div class="w-full aspect-ratio-16/9 relative">
              <img
                sizes="1px"
                :src="project.cover"
                class="absolute w-full h-full inset-0 object-cover"
              />
            </div>
          </template>
          <template v-else>
            <div class="aspect-ratio-16/9 w-full bg-gray-200" />
          </template>
        </div>
      </RouterLink>
      <BaseCardBody>
        <ul class="uppercase">
          <li class="flex justify-between items-baseline text-sm">
            <span class="block font-medium capitalize text-gray-600">Impressions</span>
            <span>{{ impressions }}</span>
          </li>

          <li class="mt-2 flex justify-between items-baseline text-sm">
            <span class="block font-medium capitalize text-gray-600">Reach</span>
            <span>{{ reach }}</span>
          </li>

          <li class="mt-2 flex justify-between items-baseline text-sm">
            <span class="block font-medium capitalize text-gray-600">Pageviews</span>
            <span>{{ inlineLinkClicks }}</span>
          </li>

          <li class="mt-2 flex justify-between items-baseline text-sm">
            <span class="block font-medium capitalize text-gray-600">Contact Requests</span>
            <span>{{ contactRequestsCount }}</span>
          </li>
        </ul>
      </BaseCardBody>
      <BaseCardBody class="flex justify-between">
        <RouterLink :to="to" class="btn btn-brand rounded-sm"> More ... </RouterLink>

        <RouterLink
          :to="{
            name: 'accounts.projects.project.pipelines',
            params: {
              ...$route.params,
              projectId: project.uuid,
            },
          }"
          class="btn btn-secondary rounded-sm ml-2"
        >
          Pipeline
        </RouterLink>
      </BaseCardBody>
    </BaseCard>
  </div>
</template>

<script>
import Numbro from '@/utils/numbro';
import mediableMixin from '@/mixins/mediableMixin';

export default {
  mixins: [mediableMixin],

  props: {
    project: {
      type: Object,
      required: true,
    },
  },

  computed: {
    channels() {
      return this.project.channels;
    },
    facebookCampaigns() {
      return this.channels
        .filter((channel) => 'facebookCampaign' in channel)
        .map((channel) => channel.facebookCampaign);
    },
    facebookCampaignAggregates() {
      return this.facebookCampaigns.reduce(
        (acc, cur) => ({
          ...acc,
          impressions: acc.impressions + cur.lifetimeReport.impressions,
          reach: acc.reach + cur.lifetimeReport.reach,
          clicks: acc.clicks + cur.lifetimeReport.inlineLinkClicks,
        }),
        {
          impressions: 0,
          reach: 0,
          clicks: 0,
        }
      );
    },
    gaCampaigns() {
      return this.channels
        .filter((channel) => 'gaCampaign' in channel)
        .map((channel) => channel.gaCampaign);
    },
    gaCampaignAggregates() {
      return this.gaCampaigns.reduce(
        (acc, cur) => ({
          ...acc,
          impressions: acc.impressions + (cur.lifetimeReport?.impressions || 0),
          reach: acc.reach + (cur.lifetimeReport?.interactions || 0),
          clicks: acc.clicks + (cur.lifetimeReport?.clicks || 0),
        }),
        {
          impressions: 0,
          reach: 0,
          clicks: 0,
        }
      );
    },
    aggregates() {
      const { contactRequestsCount = 0 } = this.project || {};
      return [this.facebookCampaignAggregates, this.gaCampaignAggregates].reduce(
        (acc, cur) => ({
          ...acc,
          impressions: acc.impressions + cur.impressions,
          reach: acc.reach + cur.reach,
          clicks: acc.clicks + cur.clicks,
        }),
        {
          impressions: 0,
          reach: 0,
          clicks: 0,
          contactRequestsCount,
        }
      );
    },
    impressions() {
      return this.formatNumber(this.aggregates.impressions);
    },
    reach() {
      return this.formatNumber(this.aggregates.reach);
    },
    inlineLinkClicks() {
      return this.formatNumber(this.aggregates.clicks);
    },
    contactRequestsCount() {
      return this.formatNumber(this.aggregates.contactRequestsCount);
    },
    cover() {
      if (!this.project || !this.project.media || !Array.isArray(this.project.media)) {
        return null;
      }

      const cover = this.project.media.find((medium) => medium.collection === 'covers');

      return cover || null;
    },
    to() {
      const { params } = this.$route;
      return {
        name: 'accounts.projects.project',
        params: { ...params, projectId: this.project.uuid },
      };
    },
  },

  methods: {
    formatNumber(value) {
      return Numbro(value || 0).format({
        thousandSeparated: true,
        average: value > 100000,
        mantissa: value > 100000 ? 1 : 0,
      });
    },
  },
};
</script>
